// The sim host: glue between the runtime (clock, RNG streams, actions), the one kernel game and the collision world.
// A scene (the A1 lab, gate A1.3; the A2 valley, gates A2.2 and A2.3) brings its layers, seed, action map, modules
// and content; the host builds the collision world, the RNG, the actions and the kernel game (`createCore`, with the
// error policy and services) once. One runtime sim step = the scene reads its input and hands it to the game, then
// the kernel ticks every system (the core's, the scene's, the planted thrower's), the scene's physics step last.
// Saves carry everything that decides the next step; the hash covers the resulting state.
// The pattern follows spike 001's kernel-host.ts (read, not imported).
import { hashState, hashWorld, type Game, type Save } from "@rpg/kernel";
import { Actions, bindActions, createRng, type ActionMap, type ActionsState, type ClockState, type InputTape, type RngState, type RngStreams, type Runtime } from "@rpg/runtime";
import { createCollisionWorld, type BodySpec, type CollisionSnapshot, type CollisionWorld, type LayerTable, type Rapier } from "@rpg/collision";
import { createCore, type CoreReport, type GameEvents, type SceneParts } from "../core";

/** The scenes: the A1 lab (`?scene=lab`), the A2 test valley (the default), phase B's dojo (`?scene=dojo`), the B4
 *  arena (`?scene=arena`), the B5 ragdoll lab (`?scene=ragdoll`), phase C's streamed valley (`?scene=world`), the
 *  C4 places lab (`?scene=places`), the C3 sky lab (`?scene=sky`), the C5 things lab (`?scene=things`),
 *  phase D1's market lab (`?scene=market`) and
 *  town (`?scene=town`: the streamed valley with the townsfolk and the cutscenes), and the D2 hud lab (`?scene=hud`: the
 *  streamed valley with the discovery module), and the story lab (`?scene=story`). Since phase D joined, `town`, `hud`
 *  and `story` name the default world. */
export type SceneName = "lab" | "valley" | "dojo" | "arena" | "ragdoll" | "world" | "places" | "sky" | "things" | "market" | "town" | "hud" | "story";

/** What a scene builds once its world, RNG and actions exist. */
export interface SceneSetup {
  /** Its modules and content for the one kernel game. */
  parts: SceneParts;
  /** The kernel component types the state hash covers. */
  hashed: readonly string[];
  /** Once per sim step before the tick: read the actions and hand the input to the game. */
  beforeTick(game: Game<GameEvents>): void;
  /** Once per sim step after the tick (optional: bots and probes watch here). */
  afterTick?(game: Game<GameEvents>): void;
  /** After a restore has put back the kernel's slices and then the physics (optional: a scene squares a save from an
   *  older composition with its own, e.g. the valley drops a mount body it no longer has). */
  afterRestore?(): void;
  /** The bodies the view draws. */
  bodies: BodySpec[];
}

/** A scene: the values its world is built from, and how to build it. */
export interface SceneDef {
  name: SceneName;
  layers: LayerTable;
  seed: number;
  actions: ActionMap;
  /** Rapier's solver iterations for its world (default Rapier's; the ragdoll lab's joints want more). */
  solverIterations?: number;
  setup(collision: CollisionWorld, rng: RngStreams, actions: Actions): SceneSetup;
}

/** Everything a save at any step needs to continue identically. JSON-safe; `format` and `scene` are checked on restore. */
export interface SimSave {
  format: "green-valley-sim@2";
  /** The scene the save belongs to: a save restores only into the same scene. */
  scene: SceneName;
  /** The step the save was taken at (also inside `runtime.clock`). */
  step: number;
  /** The kernel's namespaced module slices. */
  kernel: Save;
  /** The runtime's clock and named RNG streams. */
  runtime: { clock: ClockState; rng: Record<string, RngState> };
  /** The sim-time input state (injected holds, latched presses, stick overrides). */
  input: ActionsState;
  /** The Rapier world (base64) with its ID map. */
  physics: CollisionSnapshot;
  /** The camera rig's snapshot (yaw, pitch offset, profile requests), when the scene has one: a view's state, so
   *  not in the hash; the sim's own copy of the yaw is in the kernel. Absent in saves without a camera. */
  camera?: unknown;
}

export const SIM_SAVE_FORMAT = "green-valley-sim@2" as const;

/** CPU time of the host's sim step (the kernel's systems with the Rapier step), over every step since the last reset. */
export interface SimStepStats {
  samples: number;
  meanMs: number;
  p95Ms: number;
  maxMs: number;
}

export interface SimHost {
  readonly scene: SceneDef;
  readonly setup: SceneSetup;
  readonly collision: CollisionWorld;
  readonly actions: Actions;
  readonly rng: RngStreams;
  readonly game: Game<GameEvents>;
  /** The kernel's report (policy, systems, services) for `__rpg.kernel()`. */
  kernel(): CoreReport;
  /** The deterministic state hash: step, kernel components, every body, the RNG streams. */
  hash(): string;
  /** The hash's parts, to find which part diverged; `props` covers the dynamic bodies only. */
  hashParts(): { step: number; kernel: string; physics: string; props: string; rng: string };
  save(): SimSave;
  restore(s: SimSave): void;
  stepStats(reset?: boolean): SimStepStats;
}

const MAX_SAMPLES = 1 << 15;

/** `actions`: the game's input (src/input, gate A2.1), already bound to the runtime, with its buffer; without it (Node,
 *  tools/*.test.ts) the host makes and binds its own from the scene's action map. */
export function createSimHost(rapier: Rapier, runtime: Runtime, scene: SceneDef, given?: Actions): SimHost {
  const collision = createCollisionWorld(rapier, { dt: runtime.clock.dt, layers: scene.layers, ...(scene.solverIterations ? { solverIterations: scene.solverIterations } : {}) });
  const rng = createRng(scene.seed);
  const actions = given ?? new Actions(scene.actions, { now: () => runtime.time });
  if (!given) bindActions(runtime, actions);
  const setup = scene.setup(collision, rng, actions);
  const core = createCore(setup.parts);
  const game = core.game;

  const cpu = new Float64Array(MAX_SAMPLES);
  let n = 0;
  runtime.on(
    "sim",
    (c) => {
      const t0 = performance.now();
      setup.beforeTick(game);
      game.tick(c.dt);
      setup.afterTick?.(game);
      cpu[n++ % MAX_SAMPLES] = performance.now() - t0;
    },
    { name: "sim: host step" },
  );

  const parts = () => ({
    step: runtime.clock.step,
    kernel: hashWorld(game.world, setup.hashed),
    physics: collision.hash(),
    rng: hashState(rng.snapshot()),
  });
  return {
    scene,
    setup,
    collision,
    actions,
    rng,
    game,
    kernel: core.report,
    hash: () => hashState(parts()),
    hashParts: () => ({ ...parts(), props: hashState(collision.bodyStates().filter((b) => b.kind === "dynamic")) }),
    save: () => ({
      format: SIM_SAVE_FORMAT,
      scene: scene.name,
      step: runtime.clock.step,
      kernel: game.serialize(),
      runtime: { clock: runtime.clock.snapshot(), rng: rng.snapshot() },
      input: actions.snapshot(),
      physics: collision.snapshot(),
    }),
    restore(s) {
      if (!s || s.format !== SIM_SAVE_FORMAT) throw new Error(`restore: not a ${SIM_SAVE_FORMAT} save (format ${String((s as { format?: unknown })?.format)})`);
      if (s.scene !== scene.name) throw new Error(`restore: a "${s.scene}" save can't load into the "${scene.name}" scene (open ?scene=${s.scene})`);
      game.restore(s.kernel);
      runtime.clock.restore(s.runtime.clock);
      rng.restore(s.runtime.rng);
      actions.restore(s.input);
      collision.restore(s.physics);
      setup.afterRestore?.();
    },
    stepStats(reset = false) {
      const k = Math.min(n, MAX_SAMPLES);
      const v = Array.from(cpu.subarray(0, k)).sort((a, b) => a - b);
      const out = {
        samples: k,
        meanMs: k ? v.reduce((a, b) => a + b, 0) / k : 0,
        p95Ms: k ? v[Math.min(k - 1, Math.floor(k * 0.95))]! : 0,
        maxMs: k ? v[k - 1]! : 0,
      };
      if (reset) n = 0;
      return out;
    },
  };
}

export type { InputTape };
